import type { ReactNode } from "react"
import { Navigate, useLocation } from "react-router"

import { useAuth } from "./AuthProvider"

interface ProtectedRouteProps {
  children: ReactNode
  /**
   * When set, additionally requires a matching role. Used by the admin
   * panel (`admin`).
   */
  requiredRole?: "admin"
}

/**
 * Client-side route guard. While `/auth/me` is in flight we render a
 * lightweight placeholder — no spinner thrash. On 401 we redirect to
 * `/login`, preserving the requested location in state so the login form
 * can bounce the user back after a successful submit.
 *
 * The redirect is a client-side `<Navigate/>` (T2.4 acceptance: "no full
 * reload"), not a `window.location.href`, so React's query cache and
 * router state survive the hop.
 */
export function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const { user, isLoading, isAdmin } = useAuth()
  const location = useLocation()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-brand-fg/60">
        Loading…
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />
  }

  if (requiredRole === "admin" && !isAdmin) {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}
